import React from "react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import ADIColors from "@NA/Utils/ADIColors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { connect } from "react-redux";
import { getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { css } from "styled-components";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import { isCoveoSearchEnabled } from "@NA/Utilities/CoveoUtilities";

interface OwnProps {
    goToUrl: (url: string) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    searchHistoryEnabled: getSettingsCollection(state).searchSettings.searchHistoryEnabled,
    searchPath: getSettingsCollection(state).searchSettings.searchPath,
    session: state.context.session,
    isCoveoUser: state.context.session?.userRoles?.includes("Coveo-Access"),
    settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
});

export interface pastSearchStyles {
    mainGridContainer?: GridContainerProps;
    mainGridItem?: GridItemProps;
    mainHeading?: TypographyProps;
    linkGridItem?: GridItemProps;
    searchList?: TypographyProps;
    link?: TypographyProps;
}

const pastSearchStyles: pastSearchStyles = {
    mainGridContainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center; 
            border-radius: 5px;
            margin:50px 0 82px;
            ${({ theme }) => { return breakpointMediaQueries(
                theme, [null, null, css` margin:40px 0 70px; `,],"max",); 
            }}
            ${GridOffset} {
                justify-content: center;
                gap: 10px;
            }
        `,
    },
    searchList: {
        css: css`
        display: flex;
        justify-content: center;
        align-items: center;
        ${({ theme }) => { return breakpointMediaQueries(
            theme, [
                css` flex-basis: 100%; `,
                css` flex-basis: 100%; `,
                css` flex-basis: auto;justify-content: flex-start; `,],); 
        }}
        &:nth-child(2), &:nth-child(4), &:nth-child(6) {
            ${({ theme }) => { return breakpointMediaQueries(
                theme, [
                    null,
                    null,
                    css` justify-content: flex-end; `,],); 
            }}
        }
        `,
    },
    mainHeading: {
        css: css`
            font-family: 'Nunito Sans';
            font-size: 32px;
            font-weight: bold;
            color: ${ADIColors.darkBlue1};
            text-align: center;
            margin-bottom: 20px;
            line-height: normal;
            width: 100%;
        `,
    },
    link: {
        css: css`
            margin: 0;
            ${TypographyStyle} {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: flex-start;
                padding: 8px 16px;
                border-radius: 4px;
                border: solid 1px #ddd;
                background-color: #fff;
                font-size: 14px;
                font-weight: 600;
                color: ${ADIColors.darkBlue1};
            }
            `,
        },  
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const ADIPastSearches = ({
        searchHistoryEnabled,
        searchPath,
        session,
        settingsCollection,
        isCoveoUser,
    }: Props) => {
    const isCoveoEnabled = isCoveoSearchEnabled(settingsCollection, isCoveoUser);
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const searchHistory = getSearchHistory().slice(0, 6);

    const history = useHistory();

    if (searchHistoryEnabled && searchHistory.length < 3) {
        return null;
    }

    return (
        <>
            {isAuthenticated && searchHistoryEnabled && (
                <StyledWrapper>
                    <GridContainer {...pastSearchStyles.mainGridContainer} >
                        <Typography as="h4" {...pastSearchStyles.mainHeading}>{translate("Past Searches")}</Typography>
                        {searchHistory.map(searchHistoryItem => (
                        <StyledWrapper key={searchHistoryItem.query} {...pastSearchStyles.searchList}>
                            <Link {...pastSearchStyles.link}
                                    onClick={() => history.push(`/${searchPath}${isCoveoEnabled ? `?q=${searchHistoryItem.query}` : `?query=${searchHistoryItem.query}`}`)}
                                    data-ga-promo={`home_past-searches_${searchHistoryItem.query}`}
                                >
                                {searchHistoryItem.query}
                            </Link>
                        </StyledWrapper>
                        ))}
                    </GridContainer>
            </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ADIPastSearches),
    definition: {
        group: "Common",
        displayName: "ADI Past Searches",
    },
};

export default widgetModule;

